<template>
  <form class="vokalis-grouped-aggregate-form" @submit.prevent>
    <FormSection :title="$t('groupedAggregateRowsDataSourceForm.data')" class="margin-bottom-2">
      <FormGroup
        :label="$t('groupedAggregateRowsDataSourceForm.sourceFieldLabel')"
        class="margin-bottom-2"
        small-label
        required
        horizontal
        horizontal-narrow
      >
        <Dropdown
          v-model="values.table_id"
          :show-search="true"
          fixed-items
        >
          <DropdownSection
            v-for="database in databases"
            :key="database.id"
            :title="`${database.name} (${database.id})`"
          >
            <DropdownItem
              v-for="table in database.tables"
              :key="table.id"
              :name="table.name"
              :value="table.id"
              :indented="true"
            >
              {{ table.name }}
            </DropdownItem>
          </DropdownSection>
        </Dropdown>
      </FormGroup>

      <FormGroup
        v-if="values.table_id"
        :label="$t('groupedAggregateRowsDataSourceForm.viewFieldLabel')"
        class="margin-bottom-2"
        small-label
        required
        horizontal
        horizontal-narrow
      >
        <Dropdown v-model="values.view_id" :show-search="false" fixed-items>
          <DropdownItem :name="$t('groupedAggregateRowsDataSourceForm.notSelected')" :value="null">
            {{ $t('groupedAggregateRowsDataSourceForm.notSelected') }}
          </DropdownItem>
          <DropdownItem
            v-for="view in tableViews"
            :key="view.id"
            :name="view.name"
            :value="view.id"
          >
            {{ view.name }}
          </DropdownItem>
        </Dropdown>
      </FormGroup>
    </FormSection>

    <FormSection
      v-if="values.table_id"
      :title="$t('groupedAggregateRowsDataSourceForm.series')"
      class="margin-bottom-2"
    >
      <div
        v-for="(series, index) in values.aggregation_series"
        :key="series.id"
        class="vokalis-grouped-aggregate-form__series"
      >
        <FormGroup
          class="margin-bottom-2"
          :label="$t('groupedAggregateRowsDataSourceForm.seriesFieldLabel')"
          small-label
          horizontal
          horizontal-narrow
          required
        >
          <Dropdown
            :value="series.field_id"
            :disabled="fieldsLoading || tableFields.length === 0"
            :show-search="true"
            fixed-items
            @input="onSeriesFieldIdChange(index, $event)"
          >
            <DropdownItem
              v-for="field in tableFields"
              :key="field.id"
              :name="field.name"
              :value="field.id"
            >
              {{ field.name }}
            </DropdownItem>
          </Dropdown>
        </FormGroup>

        <FormGroup
          class="margin-bottom-2"
          :label="$t('groupedAggregateRowsDataSourceForm.aggregationTypeLabel')"
          small-label
          horizontal
          horizontal-narrow
          required
        >
          <Dropdown
            :value="series.aggregation_type"
            :disabled="fieldsLoading"
            :show-search="false"
            fixed-items
            @input="onSeriesAggregationTypeChange(index, $event)"
          >
            <DropdownItem
              v-for="agg in getSeriesAggregationTypes(series.field_id)"
              :key="agg.getType()"
              :name="agg.getName()"
              :value="agg.getType()"
            >
              {{ agg.getName() }}
            </DropdownItem>
          </Dropdown>
        </FormGroup>

        <div class="vokalis-grouped-aggregate-form__series-actions">
          <button
            type="button"
            class="vokalis-grouped-aggregate-form__btn"
            :disabled="values.aggregation_series.length <= 1"
            @click="deleteSeries(index)"
          >
            {{ $t('groupedAggregateRowsDataSourceForm.removeSeries') }}
          </button>
        </div>
        <div class="vokalis-grouped-aggregate-form__divider"></div>
      </div>

      <button
        type="button"
        class="vokalis-grouped-aggregate-form__btn vokalis-grouped-aggregate-form__btn--primary"
        :disabled="!values.table_id"
        @click="addSeries"
      >
        {{ $t('groupedAggregateRowsDataSourceForm.addSeries') }}
      </button>
    </FormSection>

    <FormSection v-if="values.table_id" :title="$t('groupedAggregateRowsDataSourceForm.groupBy')" class="margin-bottom-2">
      <FormGroup
        :label="$t('groupedAggregateRowsDataSourceForm.groupByLabel')"
        small-label
        horizontal
        horizontal-narrow
      >
        <Dropdown v-model="groupByFieldId" :show-search="true" fixed-items>
          <DropdownItem :name="$t('groupedAggregateRowsDataSourceForm.notSelected')" :value="null">
            {{ $t('groupedAggregateRowsDataSourceForm.notSelected') }}
          </DropdownItem>
          <DropdownItem
            v-for="field in compatibleGroupByFields"
            :key="field.id"
            :name="field.name"
            :value="field.id"
          >
            {{ field.name }}
          </DropdownItem>
        </Dropdown>
      </FormGroup>
    </FormSection>

    <FormSection v-if="values.table_id" class="margin-bottom-2">
      <FormGroup
        :label="$t('groupedAggregateRowsDataSourceForm.sortMvp')"
        small-label
        horizontal
        horizontal-narrow
      >
        <Dropdown v-model="sortDirection" :show-search="false" fixed-items>
          <DropdownItem :name="'ASC'" value="ASC">ASC</DropdownItem>
          <DropdownItem :name="'DESC'" value="DESC">DESC</DropdownItem>
        </Dropdown>
      </FormGroup>
    </FormSection>
  </form>
</template>

<script>
import tableFields from '@baserow/modules/database/mixins/tableFields'

/** Detach from Vuex: never bind v-model to objects shared with the store. */
function clonePlain(value) {
  if (value === null || value === undefined) {
    return value
  }
  return JSON.parse(JSON.stringify(value))
}

function normalizeId(value) {
  if (value === null || value === undefined || value === '') {
    return null
  }
  const parsed = Number(value)
  return Number.isNaN(parsed) ? value : parsed
}

export default {
  name: 'VokalisGroupedAggregateRowsDataSourceForm',
  mixins: [tableFields],
  props: {
    dashboard: { type: Object, required: true },
    widget: { type: Object, required: true },
    dataSource: { type: Object, required: true },
    storePrefix: { type: String, required: false, default: '' },
    loading: { type: Boolean, required: false, default: true },
  },
  data() {
    return {
      allowedGroupByFieldTypes: [
        'text',
        'long_text',
        'url',
        'email',
        'number',
        'rating',
        'boolean',
        'phone_number',
        'autonumber',
        'single_select',
      ],
      values: {
        table_id: null,
        view_id: null,
        aggregation_series: [],
        aggregation_group_bys: [],
        aggregation_sorts: [],
      },
      nextSeriesId: 1,
      supportedAggregationTypes: [
        'count',
        'not_empty_count',
        'checked_count',
        'not_checked_count',
        'empty_percentage',
        'not_empty_percentage',
        'checked_percentage',
        'not_checked_percentage',
        'unique_count',
      ],
      sortDirection: 'DESC',
      isSyncingFromDataSource: false,
    }
  },
  computed: {
    integration() {
      return this.$store.getters[
        `${this.storePrefix}dashboardApplication/getIntegrationById`
      ](this.dataSource.integration_id)
    },
    databases() {
      return this.integration?.context_data?.databases || []
    },
    databaseSelected() {
      return this.databases.find((database) =>
        database.tables?.some((table) => table.id === this.values.table_id)
      )
    },
    tableViews() {
      return (
        this.databaseSelected?.views?.filter(
          (view) => view.table_id === this.values.table_id
        ) || []
      )
    },
    compatibleGroupByFields() {
      return this.tableFields.filter((field) =>
        this.allowedGroupByFieldTypes.includes(field.type)
      )
    },
    groupByFieldId: {
      get() {
        return this.values.aggregation_group_bys?.[0]?.field_id ?? null
      },
      set(val) {
        const normalizedId = normalizeId(val)
        this.values.aggregation_group_bys = val
          ? [{ field_id: normalizedId }]
          : []
        if (!normalizedId) {
          this.values.aggregation_sorts = []
        }
        this.emitValues()
      },
    },
    primaryTableField() {
      return this.tableFields.find((f) => f.primary === true) || null
    },
  },
  watch: {
    dataSource: {
      immediate: true,
      handler(dataSource) {
        this.isSyncingFromDataSource = true
        this.values.table_id = normalizeId(dataSource.table_id)
        this.values.view_id = normalizeId(dataSource.view_id)
        this.values.aggregation_sorts = clonePlain(
          dataSource.aggregation_sorts || []
        )
        this.values.aggregation_group_bys = (clonePlain(
          dataSource.aggregation_group_bys || []
        ) || []).map((groupBy) => ({
          ...groupBy,
          field_id: normalizeId(groupBy?.field_id),
        }))
        this.values.aggregation_series = (clonePlain(
          dataSource.aggregation_series || []
        ) || []).map((series) => ({
          ...series,
          field_id: normalizeId(series?.field_id),
        }))

        // Ensure at least one series.
        if (this.values.aggregation_series.length === 0) {
          this.nextSeriesId = 1
          this.values.aggregation_series = [
            {
              id: this.nextSeriesId,
              field_id: null,
              aggregation_type: 'sum',
            },
          ]
          this.nextSeriesId = 2
        } else {
          const maxId = Math.max(
            ...this.values.aggregation_series.map((s) => s.id || 0),
            0
          )
          this.nextSeriesId = maxId + 1
        }

        // Defensive: old/stale configs can reference unsupported group-by fields.
        if (
          this.groupByFieldId !== null &&
          !this.compatibleGroupByFields.some((f) => f.id === this.groupByFieldId)
        ) {
          this.values.aggregation_group_bys = []
        }
        this.$nextTick(() => {
          this.isSyncingFromDataSource = false
        })
      },
      deep: true,
    },
    // When the selected table changes, reset view/series/groupBy.
    'values.table_id'(newVal, oldVal) {
      // Ignore internal dataSource -> form synchronization.
      if (this.isSyncingFromDataSource) {
        return
      }
      const normalizedNew = normalizeId(newVal)
      const normalizedOld = normalizeId(oldVal)
      if (normalizedNew !== normalizedOld) {
        this.values.view_id = null
        this.values.aggregation_series = this.values.aggregation_series.map(
          (s) => ({
            ...s,
            field_id: null,
            aggregation_type: 'sum',
          })
        )
        this.values.aggregation_group_bys = []
        this.emitValues()
      }
    },
    // Auto-fix aggregation type if current one is no longer valid.
    'values.aggregation_series': {
      handler() {
        this.emitValues()
      },
      deep: true,
    },
    sortDirection: {
      handler() {
        // The backend MVP currently ignores sorting. We still persist the
        // configuration so the contract stays forward-compatible.
        const groupFieldId = this.groupByFieldId
        if (!groupFieldId) {
          this.values.aggregation_sorts = []
          this.emitValues()
          return
        }
        this.values.aggregation_sorts = [
          {
            sort_on: 'GROUP_BY',
            reference: `field_${groupFieldId}`,
            direction: this.sortDirection,
          },
        ]
        this.emitValues()
      },
    },
  },
  methods: {
    getTableId() {
      return this.values.table_id
    },
    isFormValid() {
      if (!this.values.table_id) return false
      if (!this.values.aggregation_series?.length) return false
      if (
        this.groupByFieldId !== null &&
        !this.compatibleGroupByFields.some((f) => f.id === this.groupByFieldId)
      ) {
        return false
      }
      for (const series of this.values.aggregation_series) {
        if (!series.field_id) return false
        if (!series.aggregation_type) return false
      }
      return true
    },
    reset() {
      // Keep dataSource-driven state; just re-emit.
      this.emitValues()
    },
    touch() {
      // No-op for MVP.
    },
    emitValues() {
      if (this.isSyncingFromDataSource) {
        return
      }
      this.$emit('values-changed', {
        table_id: this.values.table_id,
        view_id: this.values.view_id,
        aggregation_series: clonePlain(this.values.aggregation_series),
        aggregation_group_bys: clonePlain(this.values.aggregation_group_bys),
        aggregation_sorts: clonePlain(this.values.aggregation_sorts),
      })
    },
    onSeriesFieldIdChange(index, fieldId) {
      const current = this.values.aggregation_series[index]
      this.values.aggregation_series[index] = {
        ...current,
        field_id: normalizeId(fieldId),
      }
    },
    onSeriesAggregationTypeChange(index, aggregationType) {
      const current = this.values.aggregation_series[index]
      this.values.aggregation_series[index] = {
        ...current,
        aggregation_type: aggregationType,
      }
    },
    addSeries() {
      this.values.aggregation_series.push({
        id: this.nextSeriesId++,
        field_id: null,
        aggregation_type: 'sum',
      })
      this.emitValues()
    },
    deleteSeries(index) {
      this.values.aggregation_series.splice(index, 1)
      this.emitValues()
    },
    getSeriesAggregationTypes(fieldId) {
      if (!fieldId) return []
      const selectedField = this.tableFields.find((f) => f.id === fieldId)
      if (!selectedField) return []

      return this.$registry
        .getOrderedList('viewAggregation')
        .filter(
          (agg) =>
            agg.fieldIsCompatible(selectedField) &&
            this.supportedAggregationTypes.includes(agg.getType())
        )
    },
  },
}
</script>

<style scoped>
.vokalis-grouped-aggregate-form__divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.08);
  margin: 12px 0;
}
.vokalis-grouped-aggregate-form__series-actions {
  margin-bottom: 4px;
}
.vokalis-grouped-aggregate-form__btn {
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: rgba(255, 255, 255, 0.06);
  color: inherit;
  padding: 7px 10px;
  border-radius: 8px;
  cursor: pointer;
}
.vokalis-grouped-aggregate-form__btn--primary {
  background: rgba(81, 144, 239, 0.16);
  border-color: rgba(81, 144, 239, 0.5);
}
.vokalis-grouped-aggregate-form__btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>

